/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useCallback, useState, Fragment } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withTheme, withStyles, TextField, MenuItem } from '@material-ui/core';
import { withRouter } from 'react-router-dom';
import Spinner from 'modules/common/spinner.component';
import { getExpenseSummaries } from 'modules/expenses/store/expenses.actions';
import { memoize, chain } from 'lodash';
import auth from 'auth/auth';
import ExpensesGridComponent from './expensesGrid.component';
import CreateExpenseDialogComponent from './createExpenseDialog.component';
import { Box, Button } from '@mui/material';
import { fetchEnumerations } from 'modules/authorizations/utils/fetchDataDetailsService';
import { getAllCountries, getAllCurrencies, getAllEnumerations, getAllProvinces, getAllSaleTypes, getAllWeightTypes } from 'modules/enumerations/store/enumerationActions';
import { getVendors } from 'modules/common/common.actions';
import { getAuthorizationSummaries } from 'modules/authorizations/store/actions/authorizations.actions';
import { setPageTitle } from 'modules/layout/layout.actions';

const styles = () => ({
  headerContainer: { display: 'flex', gap: 12, marginBottom: 12 },
  textField: { minWidth: 220 }
});

export const projectSummariesToGrid = (summaries = []) =>
  summaries && Array.isArray(summaries)
    ? summaries.map(s => ({
        statusId: s.statusId,
        authorizationId: s.authorizationFileId,
        authName: s.authorizationFileFullName || '',
        expenseId: s.expenseId,
        dateSubmitted: s.submittedDate,
        description: s.description || '',
        total: s.totalAmount ?? 0,
        expenseAnalyst: s.expenseAnalystFullName || '',
        numberOfLineItems: s.numberOfLineItems ?? 0
      }))
    : [];

const ExpenseSummariesPage = props => {
  const { isLoadingExpenseSummaries, expenseSummaries, getExpenseSummaries, history, clients, classes, enumerations, vendors, authorizationSummaries } = props;

  const [clientId, setClientId] = useState(-1);
  const [expenseAnalystId, setExpenseAnalystId] = useState(-1);
  const [showCreateModal, setShowCreateModal] = useState(false);

  useEffect(() => {
    async function fetchData () {
      if (!enumerations.allEnums || (enumerations.allEnums && !enumerations.allEnums.currencies)) {
        await fetchEnumerations(props);
      }

      if (!authorizationSummaries) {
        await props.getAuthorizationSummaries();
      }

      if (!vendors) {
        await props.getVendors();
      }
    }

    props.setPageTitle('Expenses');
    fetchData();
    getExpenseSummaries();
  }, [getExpenseSummaries]);

  const onSelect = useCallback(
    expenseId => {
      if (expenseId) history.push(`/expenses/${expenseId}`);
    },
    [history]
  );

  const rows = projectSummariesToGrid(expenseSummaries);

  const getClientList = memoize(clientsArr => {
    const arr = Array.isArray(clientsArr) ? clientsArr : [];
    return chain(arr)
      .uniqBy('description')
      .filter(c => !!c.description)
      .sortBy(c => String(c.description))
      .value();
  });

  const sortedClients = getClientList(clients);

  const sortedExpenseAnalysts = [
    { id: 0, fullName: 'Test Analyst 1' },
    { id: 1, fullName: 'Test Analyst 2' },
    { id: 2, fullName: 'Test Analyst 3' },
    { id: 3, fullName: 'Test Analyst 4' },
    { id: 4, fullName: 'Test Analyst 5' }
  ];

  if (isLoadingExpenseSummaries) return <Spinner />;

  return (
    <Fragment>
      <div className={classes.headerContainer}>
        <TextField
          select
          label="Clients"
          className={classes.textField}
          value={clientId}
          onChange={e => setClientId(Number(e.target.value))}
          SelectProps={{ autoWidth: true }}
          variant="outlined"
        >
          <MenuItem value={-1}>
            <em>All Clients</em>
          </MenuItem>
          {sortedClients.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.description}
            </MenuItem>
          ))}
        </TextField>
        {auth && auth.userHasRole('admin') && (
          <TextField
            select
            label="Expense Analyst"
            className={classes.textField}
            value={expenseAnalystId}
            onChange={e => setExpenseAnalystId(Number(e.target.value))}
            SelectProps={{ autoWidth: true }}
            variant="outlined"
          >
            <MenuItem value={-1}>
              <em>All Expense Analysts</em>
            </MenuItem>
            {sortedExpenseAnalysts.map(option => (
              <MenuItem key={option.id} value={option.id}>
                {option.fullName}
              </MenuItem>
            ))}
          </TextField>
        )}
      </div>

      <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
        <Box sx={{ padding: '10px', display: 'flex', flexDirection: 'row' }}></Box>
          <Button
            color="secondary"
            variant="contained"
            sx={{
              maxHeight: '40px',
              marginRight: '10px',
              marginTop: '8px',
              backgroundColor: '#5D9878',
              '&:hover': { backgroundColor: '#528569' }
            }}
            onClick={() => setShowCreateModal(true)}
          >
            {' '}Create Manual Expense{' '}
          </Button>
      </Box>

      <ExpensesGridComponent expenses={rows} isLoading={isLoadingExpenseSummaries} onSelect={onSelect} history={history} />

      {enumerations.allEnums && enumerations.allEnums.currencies && (
        <CreateExpenseDialogComponent
          open={showCreateModal}
          onCancel={() => setShowCreateModal(false)}
          history={props.history}
        />
      )}
    </Fragment>
  );
};

const mapStateToProps = state => {
  const { expenseSummaries, isLoadingExpenseSummaries } = state.expenses;
  const { clients } = state.clients;
  const { vendors } = state.common;
  const { authorizationSummaries } = state.authorizations;
  const enumerations = state.enumerations;
  return { expenseSummaries, isLoadingExpenseSummaries, clients, enumerations, vendors, authorizationSummaries };
};

export default compose(
  withStyles(styles), 
  withTheme, 
  withRouter, 
  connect(mapStateToProps, 
    { 
      getExpenseSummaries,
      getAllEnumerations,
      getAllSaleTypes,
      getAllWeightTypes,
      getAllProvinces,
      getAllCurrencies,
      getAllCountries,
      getAuthorizationSummaries,
      getVendors,
      setPageTitle
  })
)(ExpenseSummariesPage);
